import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { useParams, useHistory, Link, useLocation } from "react-router-dom";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import {
  getAnimatedIllustrationDetails,
  updateAnimatedIllustration,
} from "../api/animatedIllustrationsApi";
import styles from "./UpdateAnimatedIllustrationPage.module.css";
import AnimatedAssetSelector from "../../common/components/AnimatedAssetSelector";

function UpdateAnimatedIllustrationPage() {
  const history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // Input value states
  const [selectedProductCategory, setSelectedProductCategory] = useState("");
  const [
    selectedAnimatedIllustrationCategory,
    setSelectedAnimatedIllustrationCategory,
  ] = useState("");
  const [animatedIllustrationName, setAnimatedIllustrationName] =
    useState(null);
  const [animatedIllustrationFile, setAnimatedIllustrationFile] =
    useState(null);

  // Error states
  const [productCategoryDropdownError, setProductCategoryDropdownError] =
    useState(null);
  const [
    animatedIllustrationCategoryDropdownError,
    setAnimatedIllustrationCategoryDropdownError,
  ] = useState(null);
  const [animatedIllustrationNameError, setAnimatedIllustrationNameError] =
    useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  // Icon State
  const [animatedIllustrationDetailsObj, setAnimatedIllustrationDetailsObj] =
    useState(null);

  // Page loading state
  const [isLoading, setIsLoading] = useState(true);

  // Notification state
  const [notification, setNotification] = useState(null);

  // Params state
  const { id } = useParams();

  function buildNotification() {
    return {
      title: "Animated Illustration updated",
      content: `Animated Illustration for ${animatedIllustrationDetailsObj.combined_name} has been updated successfully`,
    };
  }

  const clearHistoryNotificationState = (history) => {
    let state = { ...history.location.state };
    delete state.notification;
    history.replace({
      ...history.location,
      state,
    });
  };

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    //Check if icon file is selected
    if (animatedIllustrationFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("A file must be selected");
    }

    if (animatedIllustrationFile) {
      setIsLoading(true);
      try {
        // TODO: Replace with updateAnimatedIllustrationMethod
        await updateAnimatedIllustration(
          user.uid,
          id,
          animatedIllustrationFile,
          animatedIllustrationDetailsObj.combined_name
        );
        setIsLoading(false);
        history.push(
          `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}/${id}`,
          {
            notification: buildNotification(),
          }
        );
      } catch (error) {
        setIsLoading(false);
        if (error.name === "FirebaseError") {
          console.log(error.message);
        } else if (
          error.message ===
          "Animated Illustration with the same name already exists"
        ) {
          setAnimatedIllustrationNameError(error.message);
        }
      }
    }
  };

  useEffect(() => {
    // TODO: Replace with get animated Illustration details method
    getAnimatedIllustrationDetails(id)
      .then((doc) => {
        setAnimatedIllustrationDetailsObj(doc);
        setSelectedProductCategory({
          id: "0",
          key: doc.name.product_category,
        });
        setSelectedAnimatedIllustrationCategory({
          id: "0",
          key: doc.name.animated_illustration_category,
        });
        setAnimatedIllustrationName(doc.name.animated_illustration_name);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log(error);
      });
    if (location.state && location["state"].notification) {
      setNotification(location.state.notification);
    }
  }, [setAnimatedIllustrationDetailsObj, setIsLoading, id, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <LoadingPage />
      ) : (
        <React.Fragment>
          <div className={`panel ${notification ? "show" : ""}`}>
            <div className={`animateFlag ${notification ? "visible" : ""}`}>
              <Flag
                success
                floating
                title={notification ? notification.title : ""}
                content={notification ? notification.content : ""}
                onDismiss={() => {
                  setNotification(null);
                  clearHistoryNotificationState(history);
                }}
              />
            </div>
          </div>
          <div className={styles.updatePageContainer}>
            <div className={styles.breadcrumbContainer}>
              <Breadcrumb>
                <Crumb
                  text="assets"
                  tagProps={{
                    to: `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}`,
                  }}
                  as={Link}
                />
                <Separator />
                <Crumb
                  text={animatedIllustrationDetailsObj.combined_name}
                  tagProps={{
                    to: `/${routes.ANIMATED_ASSETS}/${routes.ILLUSTRATIONS}/${id}`,
                  }}
                  as={Link}
                />
              </Breadcrumb>
            </div>
            <div>
              <h3 className={styles.heading}>
                {animatedIllustrationDetailsObj.combined_name}
              </h3>
              <h5 className={styles.subHeading}>Edit Animated Illustration</h5>
              <div className={styles.formRow}>
                <div className={styles.productCategoryContainer}>
                  <p className={styles.inputLabel}>Product Category</p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedProductCategory]}
                      onChange={(selectedProductCategory) => {
                        setSelectedProductCategory(selectedProductCategory);
                        setProductCategoryDropdownError(null);
                      }}
                      stretch
                      invalid={!!productCategoryDropdownError}
                      disabled={true}
                      initialSelectedItem={selectedProductCategory}
                    />
                  </div>
                  {productCategoryDropdownError ? (
                    <Flag error title={productCategoryDropdownError} />
                  ) : null}
                </div>
                <div className={styles.animatedIllustrationCategoryContainer}>
                  <p className={styles.inputLabel}>
                    Animated Illustration Category
                  </p>
                  <div className={styles.inputContainer}>
                    <Dropdown
                      items={[selectedAnimatedIllustrationCategory]}
                      onChange={(selectedAnimatedIllustrationCategory) => {
                        setSelectedAnimatedIllustrationCategory(
                          selectedAnimatedIllustrationCategory
                        );
                        setAnimatedIllustrationCategoryDropdownError(null);
                      }}
                      invalid={!!animatedIllustrationCategoryDropdownError}
                      stretch
                      disabled={true}
                      initialSelectedItem={selectedAnimatedIllustrationCategory}
                    />
                  </div>
                  {animatedIllustrationCategoryDropdownError ? (
                    <Flag
                      error
                      title={animatedIllustrationCategoryDropdownError}
                    />
                  ) : null}
                </div>
                <div className={styles.animatedIllustrationNameContainer}>
                  <p className={styles.inputLabel}>
                    Animated Illustration Name
                  </p>
                  <div className={styles.inputContainer}>
                    <TextField
                      required={!!animatedIllustrationNameError}
                      size="m"
                      placeholder="Animated Illustration Name"
                      type="text"
                      disabled={true}
                      stretch
                      onChange={(e) => {
                        setAnimatedIllustrationName(e.target.value);
                        setAnimatedIllustrationNameError(null);
                      }}
                      pattern="[a-z\d_]+"
                      value={animatedIllustrationName}
                    />
                  </div>
                  {animatedIllustrationNameError ? (
                    <Flag error title={animatedIllustrationNameError} />
                  ) : null}
                </div>
              </div>
              <div className={styles.assetSelectorContainer}>
                <div className={styles.inputContainer}>
                  <AnimatedAssetSelector
                    heading="Animated Illustration File"
                    asset={animatedIllustrationDetailsObj.img_url}
                    onAssetSelected={(file) => {
                      if (file) {
                        setAnimatedIllustrationFile(file);
                        setAssetSelectorError(null);
                      }
                    }}
                  />
                </div>
                {assetSelectorError ? (
                  <Flag error title={assetSelectorError} />
                ) : null}
              </div>
              <Stack>
                <Button primary onClick={handleSaveClick}>
                  Save
                </Button>
                <Button secondary onClick={handeCancelClick}>
                  Cancel
                </Button>
              </Stack>
            </div>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
}

export default UpdateAnimatedIllustrationPage;
